import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import {
    useGetGeoMeasurementsDevices,
    useGetMeasurementDevicesByTags,
    useGetMeasurementDevicesReadings
} from "api/api-hooks/measurement-devices-hooks";
import axios from "axios";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import L, { Icon, LatLngTuple, divIcon } from "leaflet";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { CircleMarker, Marker, ZoomControl, useMap } from "react-leaflet";

const measurementOptions = [
    {label: "None", value: "NONE"},
    {label: "Supply temperature", value: "SUPPLY_TEMPERATURE"},
    {label: "Return Temperature", value: "RETURN_TEMPERATURE"},
    {label: "Supply pressure", value: "SUPPLY_PRESSURE"},
    {label: "Return Pressure", value: "RETURN_PRESSURE"},
    {label: "Flow rate", value: "FLOW_RATE"},
    {label: "Power", value: "POWER"},
    {label: "Hot water temperature", value: "HOT_WATER_TEMPERATURE"},
];

interface MapContentProps {
    mapRef: React.MutableRefObject<any>;
}

interface ExtractedDatasetsProps {
    id: number;
    datasets: string[];
}

const MapContent = (props: MapContentProps) => {
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isInputVisible, setIsInputVisible] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [selectedMeasurementType, setSelectedMeasurementType] = useState<string>("NONE");
    const [selectedAddress, setSelectedAddress] = useState<any | null>(null);
    const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
    const [displayDialog, setDisplayDialog] = useState<boolean>(false);
    const [dialogTitle, setDialogTitle] = useState<string>("");

    const [devicesIds, setDevicesIds] = useState<number[]>([]);
    const [firstCoordinatesArray, setFirstCoordinatesArray] = useState<LatLngTuple>([0, 0]);

    const [extractedDatasets, setExtractedDatasets] = useState<ExtractedDatasetsProps[]>([]);
    const [requiredDatasets, setRequiredDatasets] = useState<string[]>([]);

    // Webpack breaks Leaflet default icon, need this to fix it.
    L.Icon.Default.mergeOptions({
        iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png").default,
        iconUrl: require("leaflet/dist/images/marker-icon.png").default,
        shadowUrl: require("leaflet/dist/images/marker-shadow.png").default,
    });

    const defaultIcon = new Icon.Default();

    const onMeasurementTypeChange = (e: {value: string}) => {
        setSelectedMeasurementType(e.value);
    };

    const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
        setSearchQuery(e.target.value);
    };

    const {
        data: devicesReadings,
        error: readingsError
    } = useGetMeasurementDevicesReadings(selectedMeasurementType, devicesIds?.join(","),{
        enabled: devicesIds?.length > 0
    });


    if (readingsError) {
        easasAlert({
          summary: "ERROR",
          detail: `Failed to fetch devices readings: ${readingsError.message}`,
          severity: "error",
        });
    }

    const {data: recentData, error: recentDataError} = useGetRecentData(requiredDatasets, {
        options: {
            enabled: displayDialog,
            staleTime: 10000
        }
    });

    if (recentDataError) {
        easasAlert({
          summary: "ERROR",
          detail: `Failed to fetch recent data: ${recentDataError.message}`,
          severity: "error",
        });
    }

    const {data: devicesGeoCoordinates, error: geoError} = useGetGeoMeasurementsDevices();
    const {data: devicesByTags, error: tagsError} = useGetMeasurementDevicesByTags();

    if (geoError) {
        easasAlert({
          summary: "ERROR",
          detail: `Failed to fetch devices geo coordinates: ${geoError.message}`,
          severity: "error",
        });
    }

    if (tagsError) {
        easasAlert({
          summary: "ERROR",
          detail: `Failed to fetch devices by tags: ${tagsError.message}`,
          severity: "error",
        });
    }

    useEffect(() => {
        if (devicesGeoCoordinates) {
            const deviceIds: number[] = devicesGeoCoordinates.map((device) => device.properties?.dbId);
            setDevicesIds(deviceIds);
        }

        if (devicesGeoCoordinates && devicesGeoCoordinates.length > 0) {
            const firstDeviceCoordinates = devicesGeoCoordinates[0].geometry?.coordinates as LatLngTuple;
            setFirstCoordinatesArray(firstDeviceCoordinates);
        }
    }, [devicesGeoCoordinates]);

    useEffect(() => {
        if (devicesByTags) {
            extractDatasets();
        }
    }, [devicesByTags]);

    const hideDialog = () => {
        setDisplayDialog(false);
    };

    const extractDatasets = () => {
        const idAndDatasetArray = devicesByTags!.map(device => {
            const datasets: string[] = device.deviceDatasets?.map(dataset => dataset.identifier as string) as string[];

            if (device.id) {
                return {id: device.id, datasets};
            }
            return {id: 0, datasets};
        });

        setExtractedDatasets(idAndDatasetArray);
    };

    const dialogHandler = (deviceId: number, title: string) => {
        const foundDataset = extractedDatasets.find(dataset => dataset.id === deviceId);
        if (foundDataset) {
            const {datasets} = foundDataset;
            setRequiredDatasets(datasets);
            setDisplayDialog(true);
            setDialogTitle(title);
        } else {
            console.error(`No dataset found with id: ${deviceId}`);
        }
    };

    const handleSelectAddress = (result: any) => {
        setSelectedAddress(result);

        const latitude = parseFloat(result.lat);
        const longitude = parseFloat(result.lon);

        if (!isNaN(latitude) && !isNaN(longitude) && props.mapRef.current) {
            props.mapRef.current.setView([latitude, longitude], 15);
        }
        setSearchResults([]);
        setSearchValue(result.display_name);
    };

    useEffect(() => {
        const apiUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            searchQuery
        )}&countrycodes=lt&limit=5`;

        axios.get(apiUrl).then((response) => {
            setSearchResults(response.data);
        });
    }, [searchQuery]);

    const map = useMap();

    const markerBounds = new L.LatLngBounds([...firstCoordinatesArray], [...firstCoordinatesArray]);
    devicesGeoCoordinates?.forEach((marker) => {
        if (marker.geometry && marker.geometry.coordinates) {
            const {coordinates} = marker.geometry;
            const leafletCoordinateArray = new L.LatLng(coordinates[0], coordinates[1]);

            markerBounds.extend(leafletCoordinateArray);
        }
    });

    map.fitBounds(markerBounds);

    return (
        <div>
            {selectedAddress && (
                <CircleMarker
                    center={[selectedAddress.lat, selectedAddress.lon]}
                    radius={12}
                    fillColor="blue"
                    fillOpacity={0.2}
                />
            )}
            {devicesGeoCoordinates &&
                devicesGeoCoordinates.length > 0 &&
                devicesGeoCoordinates?.map((device) => {
                    const position = device.geometry?.coordinates as LatLngTuple;

                    const measurementValueObj = devicesReadings && devicesReadings[device.properties?.dbId];
                    const measuredValueText = measurementValueObj
                        ? `${measurementValueObj.value} ${measurementValueObj.unit}`
                        : "N\\A";

                    const icon = divIcon({
                        className: "custom-icon",
                        html: `<div>${measuredValueText}</div>`,
                    });

                    return (
                        <Marker
                            key={device.properties?.dbId}
                            position={position}
                            icon={selectedMeasurementType === "NONE" ? defaultIcon : icon}
                            eventHandlers={{
                                click: () => {
                                    setSelectedTitle(device.properties?.title);
                                    setSelectedAddress(null);
                                },
                                dblclick: () => {
                                    dialogHandler(device.properties?.dbId, device.properties?.title);
                                },
                            }}
                        />
                    );
                })}
            <ZoomControl position="bottomright"/>

            <span className="p-input-icon-left " style={{zIndex: 1000, marginLeft: "10px", marginTop: "10px"}}>
                    <InputText
                        value={searchValue}
                        onChange={handleSearchQueryChange}
                        placeholder="Search"
                        onFocus={() => setIsInputVisible((prevState) => !prevState)}
                        style={{
                            width: `${isInputVisible ? "inherit" : "0px"}`,
                        }}
                    />
                    <ul className="absolute top-[100px] flex flex-column"
                        style={{
                            gap: "5px"
                        }}
                    >
                        {searchResults.map((result, index) => (
                            <li
                                key={index}
                                style={{cursor: "pointer", padding: "5px", backgroundColor: "white"}}
                                onClick={() => handleSelectAddress(result)}
                            >
                                {result.display_name}
                            </li>
                        ))}
                    </ul>
                </span>
            <div
                style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    zIndex: 1000,
                }}
            >
                <Dropdown
                    value={selectedMeasurementType}
                    options={measurementOptions}
                    onChange={onMeasurementTypeChange}
                />
            </div>
            {selectedTitle && !displayDialog && (
                <div id="popup">
                    <h3>{selectedTitle}</h3>
                </div>
            )}
            <Dialog
                header="Device latest measurements"
                visible={displayDialog}
                onHide={hideDialog}
                style={{width: "600px", height: "600px"}}
            >
                <h2>{dialogTitle}</h2>
                <h4 className="border-top-1 border-bottom-1 border-gray-100 pt-3 pb-3">{recentData && (recentData[0]?.data[0]?.key)?.replace("T", " ").replace("Z", "")}</h4>
                <div className="flex flex-column gap-2">
                    {recentData?.map(device => {
                        return (<div className="flex justify-content-between">
                            <p>{device.title}</p>
                            <p>{`${device.data[0].value} ${device.unit}`}</p>
                        </div>);
                    })}
                </div>

            </Dialog>
        </div>
    );
};

export default MapContent;